import React, { useState, useMemo } from "react";
import {
  LayoutDashboard,
  Users,
  Bot,
  Settings,
  ChevronDown,
  ChevronRight,
  List,
  Wrench,
  Bell,
  HelpCircle,
  MessageSquare,
  Gift,
  Menu,
  X,
} from "lucide-react";
import { useUserData } from "../../store/authStorage";
import { useNavigate, useLocation } from "react-router-dom";

export default function CommonSidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const userData = useUserData();
  
  const activeMenu = useMemo(() => {
    const path = location.pathname;
    
    // 경로와 메뉴 ID 매핑
    const pathToMenuMap: Record<string, string> = {
      '/dashboard': 'dashboard',
      '/user-management': 'user-management',
      '/agent-management': 'agent-list',
      '/agent-management/create': 'agent-create',
      '/service-management/notice': 'notice',
      '/service-management/faq': 'faq',
      '/service-management/inquiry': 'inquiry',
      '/promotion': 'promotion',
    };
    
    return pathToMenuMap[path] || 'dashboard';
  }, [location.pathname]);
  
  const [expandedMenus, setExpandedMenus] = useState({
    aiAgent: true,
    service: true,
  });
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  const toggleExpanded = (menu: string) => {
    setExpandedMenus((prev) => ({
      ...prev,
      [menu as keyof typeof expandedMenus]:
        !prev[menu as keyof typeof expandedMenus],
    }));
  };

  const handleMenuClick = (menuId: string, path: string) => {
    // 프로모션 페이지는 준비 중
    if (menuId === "promotion") {
      alert("페이지 준비중입니다.");
      return;
    }
    
    navigate(path);
    if (window.innerWidth < 1024) {
      setIsMobileOpen(false);
    }
  };

  const menuItems = [
    {
      id: "dashboard",
      label: "대시보드",
      icon: LayoutDashboard,
      path: "/",
    },
    {
      id: "user-management",
      label: "사용자관리",
      icon: Users,
      path: "/user-management",
    },
    {
      id: "aiAgent",
      label: "AI Agent관리",
      icon: Bot,
      subItems: [
        {
          id: "agent-list",
          label: "에이전트 리스트",
          icon: List,
          path: "/agent-management",
        },
        {
          id: "agent-create",
          label: "에이전트 생성툴",
          icon: Wrench,
          path: "/agent-management/create",
        },
      ],
    },
    {
      id: "service",
      label: "서비스관리",
      icon: Settings,
      subItems: [
        {
          id: "notice",
          label: "공지사항",
          icon: Bell,
          path: "/service-management/notice",
        },
        {
          id: "faq",
          label: "FAQ",
          icon: HelpCircle,
          path: "/service-management/faq",
        },
        {
          id: "inquiry",
          label: "1:1문의",
          icon: MessageSquare,
          path: "/service-management/inquiry",
        },
      ],
    },
    {
      id: "promotion",
      label: "프로모션",
      icon: Gift,
      path: "/promotion",
    },
  ];

  return (
    <>
      {/* 모바일 메뉴 토글 버튼 */}
      <button
        onClick={() => setIsMobileOpen(!isMobileOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-slate-800 rounded-lg text-white hover:bg-slate-700 transition-colors"
      >
        {isMobileOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* 모바일 오버레이 */}
      {isMobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* 사이드바 */}
      <aside
        className={`
        fixed left-0 top-0 h-full w-72 bg-slate-900 border-r border-slate-800 
        transform transition-transform duration-300 ease-in-out z-40
        ${isMobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
      `}
      >
        {/* 로고 영역 */}
        <div className="h-20 flex items-center justify-center border-b border-slate-800">
          <div className="flex items-center gap-3">
            <span className="text-xl font-bold text-white">AIvy Admin</span>
          </div>
        </div>

        {/* 메뉴 영역 */}
        <nav className="p-4 h-[calc(100%-5rem)] overflow-y-auto elegant-scrollbar">
          <ul className="space-y-2">
            {menuItems.map((item) => (
              <li key={item.id}>
                {!item.subItems ? (
                  // 단일 메뉴 아이템
                  <button
                    onClick={() => handleMenuClick(item.id, item.path)}
                    className={`
                      w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 cursor-pointer
                      ${
                        activeMenu === item.id
                          ? "bg-gradient-to-r from-purple-600/20 to-pink-600/20 text-white border border-purple-500/30"
                          : "text-slate-400 hover:text-white hover:bg-slate-800/50"
                      }
                    `}
                  >
                    <item.icon
                      size={20}
                      className={
                        activeMenu === item.id ? "text-purple-400" : ""
                      }
                    />
                    <span className="font-medium">{item.label}</span>
                  </button>
                ) : (
                  // 서브메뉴가 있는 아이템
                  <>
                    <button
                      onClick={() => toggleExpanded(item.id)}
                      className={`
                        w-full flex items-center justify-between px-4 py-3 rounded-lg transition-all duration-200 cursor-pointer
                        ${
                          item.subItems.some((sub) => activeMenu === sub.id)
                            ? "text-white bg-slate-800/30"
                            : "text-slate-400 hover:text-white hover:bg-slate-800/50"
                        }
                      `}
                    >
                      <div className="flex items-center gap-3">
                        <item.icon
                          size={20}
                          className={
                            item.subItems.some((sub) => activeMenu === sub.id)
                              ? "text-purple-400"
                              : ""
                          }
                        />
                        <span className="font-medium">{item.label}</span>
                      </div>
                      {expandedMenus[item.id as keyof typeof expandedMenus] ? (
                        <ChevronDown size={18} className="text-slate-500" />
                      ) : (
                        <ChevronRight size={18} className="text-slate-500" />
                      )}
                    </button>

                    {/* 서브메뉴 */}
                    <ul
                      className={`
                      mt-2 ml-4 space-y-1 overflow-hidden transition-all duration-300
                      ${
                        expandedMenus[item.id as keyof typeof expandedMenus]
                          ? "max-h-96"
                          : "max-h-0"
                      }
                    `}
                    >
                      {item.subItems.map((subItem) => (
                        <li key={subItem.id}>
                          <button
                            onClick={() => handleMenuClick(subItem.id, subItem.path)}
                            className={`
                              w-full flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all duration-200 cursor-pointer
                              ${
                                activeMenu === subItem.id
                                  ? "bg-gradient-to-r from-purple-600/20 to-pink-600/20 text-white border border-purple-500/30"
                                  : "text-slate-500 hover:text-white hover:bg-slate-800/50"
                              }
                            `}
                          >
                            <subItem.icon
                              size={18}
                              className={
                                activeMenu === subItem.id
                                  ? "text-purple-400"
                                  : ""
                              }
                            />
                            <span className="text-sm font-medium">
                              {subItem.label}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </li>
            ))}
          </ul>
        </nav>

        {/* 하단 정보 */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-800">
          <div className="flex items-center gap-3 px-4 py-3">
            <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center">
              <span className="text-white font-semibold text-sm">
                {userData?.nickname.charAt(0)}
              </span>
            </div>
            <div className="flex-1">
              <p className="text-white font-medium text-sm">
                {userData?.nickname}
              </p>
              <p className="text-slate-500 text-xs">
                {userData?.access_level === 0 ? "관리자" : "사용자"}
              </p>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}
